import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  MenuItem,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Collapse,
  alpha,
  useTheme,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  ShoppingCart as OrderIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  ViewColumn as ColumnsIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as ApprovedIcon,
  HourglassEmpty as PendingIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { purchaseOrdersService } from '../../services/purchaseOrders.service';
import { PurchaseOrder } from '../../types/purchaseOrders.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import apiClient from '../../services/api';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { fmtUnitPrice } from '../../utils/formatNumber';

const translations = {
  en: {
    title: 'Purchase Orders Report',
    titleAr: 'تقرير أوامر الشراء',
    filters: 'Filters',
    supplier: 'Supplier',
    warehouse: 'Warehouse',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    status: 'Status',
    allSuppliers: 'All Suppliers',
    allWarehouses: 'All Warehouses',
    allStatuses: 'All Statuses',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    columns: 'Columns',
    poNumber: 'PO #',
    poDate: 'Date',
    expectedDate: 'Expected Date',
    supplierName: 'Supplier',
    warehouseName: 'Warehouse',
    branchName: 'Branch',
    subTotal: 'Subtotal',
    discount: 'Discount',
    tax: 'Tax',
    shipping: 'Shipping',
    totalAmount: 'Total',
    statusLabel: 'Status',
    details: 'Details',
    itemCode: 'Item Code',
    itemName: 'Item',
    unitName: 'Unit',
    orderedQty: 'Ordered',
    receivedQty: 'Received',
    remainingQty: 'Remaining',
    unitPrice: 'Unit Price',
    totalPrice: 'Total',
    loading: 'Loading...',
    noData: 'No purchase orders found',
    rowsPerPage: 'Rows per page:',
    totalOrders: 'Total Orders',
    totalAmountSummary: 'Total Amount',
    totalApproved: 'Approved',
    totalPending: 'Pending',
    kwd: 'KWD',
    grandTotal: 'GRAND TOTAL',
    draft: 'Draft',
    approved: 'Approved',
    received: 'Received',
    cancelled: 'Cancelled',
    notes: 'Notes',
  },
  ar: {
    title: 'Purchase Orders Report',
    titleAr: 'تقرير أوامر الشراء',
    filters: 'الفلاتر',
    supplier: 'المورد',
    warehouse: 'المخزن',
    dateFrom: 'من تاريخ',
    dateTo: 'إلى تاريخ',
    status: 'الحالة',
    allSuppliers: 'جميع الموردين',
    allWarehouses: 'جميع المخازن',
    allStatuses: 'جميع الحالات',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    columns: 'الأعمدة',
    poNumber: 'رقم الأمر',
    poDate: 'التاريخ',
    expectedDate: 'التاريخ المتوقع',
    supplierName: 'المورد',
    warehouseName: 'المخزن',
    branchName: 'الفرع',
    subTotal: 'المجموع الفرعي',
    discount: 'الخصم',
    tax: 'الضريبة',
    shipping: 'الشحن',
    totalAmount: 'الإجمالي',
    statusLabel: 'الحالة',
    details: 'التفاصيل',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    unitName: 'الوحدة',
    orderedQty: 'الكمية المطلوبة',
    receivedQty: 'المستلم',
    remainingQty: 'المتبقي',
    unitPrice: 'سعر الوحدة',
    totalPrice: 'الإجمالي',
    loading: 'جاري التحميل...',
    noData: 'لا توجد أوامر شراء',
    rowsPerPage: 'عدد الصفوف:',
    totalOrders: 'إجمالي الأوامر',
    totalAmountSummary: 'إجمالي المبلغ',
    totalApproved: 'معتمد',
    totalPending: 'قيد الانتظار',
    kwd: 'د.ك',
    grandTotal: 'الإجمالي الكلي',
    draft: 'مسودة',
    approved: 'معتمد',
    received: 'مستلم',
    cancelled: 'ملغي',
    notes: 'ملاحظات',
  }
};

interface Supplier {
  supplierId: number;
  supplierCode: string;
  supplierName: string;
  supplierNameAr: string;
}

interface Warehouse {
  warehouseId: number;
  warehouseCode: string;
  warehouseName: string;
  warehouseNameAr: string;
}

const PurchaseOrdersReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PurchaseOrder[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  const [supplierFilter, setSupplierFilter] = useState<number | ''>('');
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);

  const [visibleColumns, setVisibleColumns] = useState({
    poNumber: true,
    poDate: true,
    supplierName: true,
    warehouseName: true,
    branchName: false,
    expectedDate: false,
    subTotal: false,
    discount: false,
    tax: false,
    shipping: false,
    totalAmount: true,
    status: true,
  });

  const [stats, setStats] = useState({ total: 0, totalAmount: 0, approved: 0, pending: 0 });

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(console.error);
    Promise.all([
      apiClient.get<Supplier[]>('/Suppliers'),
      apiClient.get<Warehouse[]>('/Warehouses'),
    ]).then(([s, w]) => { setSuppliers(s.data); setWarehouses(w.data); }).catch(console.error);
  }, []);

  useEffect(() => {
    loadData();
  }, [supplierFilter, warehouseFilter, statusFilter, dateFromFilter, dateToFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await purchaseOrdersService.getAll(
        statusFilter || undefined,
        supplierFilter || undefined,
        dateFromFilter || undefined,
        dateToFilter || undefined
      );

      // client-side warehouse filter (API has no warehouseId param for POs)
      const filtered = warehouseFilter
        ? result.filter(o => o.warehouseId === warehouseFilter)
        : result;

      setData(filtered);
      setPage(0);
      setStats({
        total: filtered.length,
        totalAmount: filtered.reduce((s, o) => s + (o.totalAmount || 0), 0),
        approved: filtered.filter(o => o.status === 'Approved' || o.status === 'Received').length,
        pending: filtered.filter(o => o.status === 'Draft').length,
      });
    } catch {
      toast.error(isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (n: number, d = 3) =>
    n.toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d });

  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');

  const getStatusColor = (s?: string): 'default' | 'warning' | 'success' | 'error' | 'info' => {
    switch (s) {
      case 'Draft': return 'warning';
      case 'Approved': return 'info';
      case 'Received': return 'success';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (s?: string) => {
    switch (s) {
      case 'Draft': return t.draft;
      case 'Approved': return t.approved;
      case 'Received': return t.received;
      case 'Cancelled': return t.cancelled;
      default: return s || '';
    }
  };

  const getPartyName = (o: PurchaseOrder) => {
    if (o.supplierId) return isRTL ? (o.supplierNameAr || o.supplierName || '-') : (o.supplierName || '-');
    if (o.custodyId) return isRTL ? (o.employeeNameAr || o.employeeName || '-') : (o.employeeName || '-');
    if (o.paymentAccountId) return isRTL ? (o.paymentAccountNameAr || o.paymentAccountName || '-') : (o.paymentAccountName || '-');
    return '-';
  };

  const toggleRow = (id: number) => {
    setExpandedRows(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ── Export Excel ────────────────────────────────────────────────────────────
  const exportToExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(t.title);

    if (companyInfo?.logoBase64) {
      try {
        const img = wb.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
        ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } });
      } catch { /* skip */ }
    }

    ws.mergeCells('C1:H1');
    const c1 = ws.getCell('C1');
    c1.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
    c1.font = { size: 18, bold: true, color: { argb: 'FF1565C0' } };
    c1.alignment = { horizontal: 'center', vertical: 'middle' };

    ws.mergeCells('C2:H2');
    ws.getCell('C2').value = companyInfo?.address || '';
    ws.getCell('C2').alignment = { horizontal: 'center' };

    ws.mergeCells('C3:H3');
    ws.getCell('C3').value = `KWD | ${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`;
    ws.getCell('C3').font = { size: 9 };
    ws.getCell('C3').alignment = { horizontal: 'center' };

    ws.getRow(4).height = 8;

    ws.mergeCells('A5:J5');
    const titleCell = ws.getCell('A5');
    titleCell.value = isRTL ? t.titleAr : t.title;
    titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
    ws.getRow(5).height = 24;

    ws.getRow(6).height = 8;

    const headers = [t.poNumber, t.poDate, t.supplierName, t.warehouseName, t.branchName, t.subTotal, t.discount, t.tax, t.shipping, t.totalAmount, t.statusLabel];
    const hRow = ws.addRow(headers);
    hRow.eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      cell.alignment = { horizontal: 'center' };
    });

    data.forEach(o => {
      ws.addRow([
        o.poNumber, formatDate(o.poDate), getPartyName(o),
        isRTL ? (o.warehouseNameAr || o.warehouseName) : (o.warehouseName || ''),
        isRTL ? (o.branchNameAr || o.branchName) : (o.branchName || ''),
        o.subTotal || 0, o.discountAmount || 0, o.taxAmount || 0,
        o.shippingCost || 0, o.totalAmount || 0, getStatusLabel(o.status),
      ]);
    });

    ws.addRow([]);
    const totalRow = ws.addRow([t.grandTotal, '', '', '', '', '', '', '', '', formatNumber(stats.totalAmount), '']);
    totalRow.eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };
    });

    ws.columns.forEach(col => { col.width = 16; });

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase-orders-${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Export PDF ───────────────────────────────────────────────────────────────
  const exportToPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text(isRTL ? t.titleAr : t.title, doc.internal.pageSize.width / 2, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`${new Date().toLocaleDateString('en-GB')}`, doc.internal.pageSize.width / 2, 22, { align: 'center' });

    autoTable(doc, {
      startY: 28,
      head: [[t.poNumber, t.poDate, t.supplierName, t.warehouseName, t.totalAmount, t.statusLabel]],
      body: data.map(o => [
        o.poNumber, formatDate(o.poDate), getPartyName(o),
        isRTL ? (o.warehouseNameAr || o.warehouseName || '') : (o.warehouseName || ''),
        formatNumber(o.totalAmount || 0), getStatusLabel(o.status),
      ]),
      headStyles: { fillColor: [21, 101, 192], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 248, 255] },
      styles: { fontSize: 8 },
    });

    doc.save(`purchase-orders-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handlePrint = () => window.print();

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>

      {/* Print Header */}
      <Box className="print-only" sx={{ mb: 3, textAlign: 'center' }}>
        {companyInfo?.logoBase64 && <img src={companyInfo.logoBase64} alt="Logo" style={{ height: 60 }} />}
        <Typography variant="h4" fontWeight="bold">{isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}</Typography>
        <Typography variant="h5" sx={{ mt: 1 }}>{isRTL ? t.titleAr : t.title}</Typography>
        <Typography variant="body2">{new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</Typography>
      </Box>

      {/* Screen Header */}
      <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), p: 1.5, borderRadius: 2 }}>
          <OrderIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="primary">{isRTL ? t.titleAr : t.title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {isRTL ? 'تقرير شامل لأوامر الشراء مع التفاصيل' : 'Comprehensive purchase orders report with details'}
          </Typography>
        </Box>
      </Box>
      <Divider className="no-print" sx={{ mb: 3 }} />

      {/* Stats Cards */}
      <Box className="no-print" sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: t.totalOrders, value: stats.total, icon: <OrderIcon />, color: 'primary.main' },
          { label: `${t.totalAmountSummary} (${t.kwd})`, value: formatNumber(stats.totalAmount), icon: <MoneyIcon />, color: 'warning.main' },
          { label: t.totalApproved, value: stats.approved, icon: <ApprovedIcon />, color: 'success.main' },
          { label: t.totalPending, value: stats.pending, icon: <PendingIcon />, color: 'error.main' },
        ].map((card, i) => (
          <Card key={i} sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{card.value}</Typography>
                  <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                </Box>
                {React.cloneElement(card.icon, { sx: { fontSize: 50, opacity: 0.3, color: card.color } })}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Filters */}
      <Paper className="no-print" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Autocomplete
            options={suppliers}
            getOptionLabel={(o) => isRTL ? o.supplierNameAr : o.supplierName}
            value={suppliers.find(s => s.supplierId === supplierFilter) || null}
            onChange={(_, v) => setSupplierFilter(v?.supplierId || '')}
            renderInput={(params) => <TextField {...params} label={t.supplier} size="small" />}
            sx={{ minWidth: 200 }}
          />
          <Autocomplete
            options={warehouses}
            getOptionLabel={(o) => isRTL ? o.warehouseNameAr : o.warehouseName}
            value={warehouses.find(w => w.warehouseId === warehouseFilter) || null}
            onChange={(_, v) => setWarehouseFilter(v?.warehouseId || '')}
            renderInput={(params) => <TextField {...params} label={t.warehouse} size="small" />}
            sx={{ minWidth: 200 }}
          />
          <TextField select size="small" label={t.status} value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">{t.allStatuses}</MenuItem>
            <MenuItem value="Draft">{t.draft}</MenuItem>
            <MenuItem value="Approved">{t.approved}</MenuItem>
            <MenuItem value="Received">{t.received}</MenuItem>
            <MenuItem value="Cancelled">{t.cancelled}</MenuItem>
          </TextField>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label={t.dateFrom} value={dateFromFilter ? new Date(dateFromFilter) : null}
              onChange={(d) => {
                const newValue = d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : '';
                setDateFromFilter(newValue);
                if (dateToFilter && newValue && dateToFilter < newValue) setDateToFilter('');
              }}
              format="dd/MM/yyyy" views={['year','month','day']} openTo="day"
              minDate={minDate} maxDate={dateToFilter ? new Date(dateToFilter) : maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label={t.dateTo} value={dateToFilter ? new Date(dateToFilter) : null}
              onChange={(d) => {
                const newValue = d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : '';
                setDateToFilter(newValue);
                if (dateFromFilter && newValue && dateFromFilter > newValue) setDateFromFilter('');
              }}
              format="dd/MM/yyyy" views={['year','month','day']} openTo="day"
              minDate={dateFromFilter ? new Date(dateFromFilter) : minDate} maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
          </LocalizationProvider>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<RefreshIcon />} onClick={loadData} variant="outlined">{t.refresh}</Button>
          <Button startIcon={<ExcelIcon />} onClick={exportToExcel} variant="contained" color="success">{t.exportExcel}</Button>
          <Button startIcon={<PdfIcon />} onClick={exportToPdf} variant="contained" color="error">{t.exportPdf}</Button>
          <Button startIcon={<PrintIcon />} onClick={handlePrint} variant="contained">{t.print}</Button>
          <Button startIcon={<ColumnsIcon />} onClick={(e) => setColumnsAnchor(e.currentTarget)} variant="outlined">{t.columns}</Button>
        </Box>
      </Paper>

      {/* Column Visibility */}
      <Popover open={Boolean(columnsAnchor)} anchorEl={columnsAnchor} onClose={() => setColumnsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t.columns}</Typography>
          <FormGroup>
            {Object.keys(visibleColumns).map(key => (
              <FormControlLabel key={key} control={
                <Checkbox size="small" checked={visibleColumns[key as keyof typeof visibleColumns]}
                  onChange={() => setVisibleColumns(p => ({ ...p, [key]: !p[key as keyof typeof visibleColumns] }))} />
              } label={t[key as keyof typeof t] || key} />
            ))}
          </FormGroup>
        </Box>
      </Popover>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  {visibleColumns.poNumber && <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.poNumber}</TableCell>}
                  {visibleColumns.poDate && <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.poDate}</TableCell>}
                  {visibleColumns.supplierName && <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.supplierName}</TableCell>}
                  {visibleColumns.warehouseName && <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.warehouseName}</TableCell>}
                  {visibleColumns.branchName && <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.branchName}</TableCell>}
                  {visibleColumns.expectedDate && <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.expectedDate}</TableCell>}
                  {visibleColumns.subTotal && <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.subTotal}</TableCell>}
                  {visibleColumns.discount && <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.discount}</TableCell>}
                  {visibleColumns.tax && <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.tax}</TableCell>}
                  {visibleColumns.shipping && <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.shipping}</TableCell>}
                  {visibleColumns.totalAmount && <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.totalAmount} ({t.kwd})</TableCell>}
                  {visibleColumns.status && <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.statusLabel}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((order, idx) => (
                  <React.Fragment key={order.purchaseOrderId}>
                    <TableRow
                      sx={{
                        cursor: 'pointer',
                        bgcolor: expandedRows.has(order.purchaseOrderId)
                          ? alpha(theme.palette.primary.main, 0.08)
                          : idx % 2 === 0 ? 'background.default' : 'action.hover',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                      }}
                      onClick={() => toggleRow(order.purchaseOrderId)}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {expandedRows.has(order.purchaseOrderId) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      {visibleColumns.poNumber && <TableCell sx={{ fontWeight: 500 }}>{order.poNumber}</TableCell>}
                      {visibleColumns.poDate && <TableCell>{formatDate(order.poDate)}</TableCell>}
                      {visibleColumns.supplierName && <TableCell>{getPartyName(order)}</TableCell>}
                      {visibleColumns.warehouseName && <TableCell>{isRTL ? (order.warehouseNameAr || order.warehouseName) : (order.warehouseName || '-')}</TableCell>}
                      {visibleColumns.branchName && <TableCell>{isRTL ? (order.branchNameAr || order.branchName) : (order.branchName || '-')}</TableCell>}
                      {visibleColumns.expectedDate && <TableCell>{order.expectedDeliveryDate ? formatDate(order.expectedDeliveryDate) : '-'}</TableCell>}
                      {visibleColumns.subTotal && <TableCell align="right">{formatNumber(order.subTotal || 0)}</TableCell>}
                      {visibleColumns.discount && <TableCell align="right">{formatNumber(order.discountAmount || 0)}</TableCell>}
                      {visibleColumns.tax && <TableCell align="right">{formatNumber(order.taxAmount || 0)}</TableCell>}
                      {visibleColumns.shipping && <TableCell align="right">{formatNumber(order.shippingCost || 0)}</TableCell>}
                      {visibleColumns.totalAmount && <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(order.totalAmount || 0)}</TableCell>}
                      {visibleColumns.status && (
                        <TableCell>
                          <Chip label={getStatusLabel(order.status)} size="small" color={getStatusColor(order.status)} />
                        </TableCell>
                      )}
                    </TableRow>

                    {/* Expanded Detail Rows */}
                    <TableRow>
                      <TableCell colSpan={13} sx={{ p: 0, border: 0 }}>
                        <Collapse in={expandedRows.has(order.purchaseOrderId)} unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: isDark ? alpha('#1565C0', 0.06) : '#E3F2FD' }}>
                            <Typography variant="caption" fontWeight="bold" color="primary" sx={{ mb: 1, display: 'block' }}>
                              {t.details} — {order.poNumber}
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#1976D2' }}>
                                  <TableCell sx={{ color: 'white', fontSize: '11px' }}>{t.itemCode}</TableCell>
                                  <TableCell sx={{ color: 'white', fontSize: '11px' }}>{t.itemName}</TableCell>
                                  <TableCell sx={{ color: 'white', fontSize: '11px' }}>{t.unitName}</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.orderedQty}</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.receivedQty}</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.remainingQty}</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.unitPrice}</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.totalPrice}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {order.details.map((d, di) => (
                                  <TableRow key={di} sx={{ bgcolor: di % 2 === 0 ? 'background.paper' : alpha('#1565C0', isDark ? 0.15 : 0.04) }}>
                                    <TableCell sx={{ fontSize: '11px' }}>{d.itemCode}</TableCell>
                                    <TableCell sx={{ fontSize: '11px' }}>{isRTL ? d.itemNameAr : d.itemName}</TableCell>
                                    <TableCell sx={{ fontSize: '11px' }}>{isRTL ? d.unitNameAr : d.unitName}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '11px' }}>{formatNumber(d.orderedQuantity, 2)}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '11px' }}>{formatNumber(d.receivedQuantity || 0, 2)}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '11px', color: (d.remainingQuantity || 0) > 0 ? 'warning.main' : 'success.main' }}>
                                      {formatNumber(d.remainingQuantity || 0, 2)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: '11px' }}>{fmtUnitPrice(d.unitPrice)}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '11px', fontWeight: 'bold' }}>{formatNumber(d.totalPrice)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {order.notes && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {t.notes}: {order.notes}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}

                {/* Grand Total Row */}
                {data.length > 0 && (
                  <TableRow sx={{ bgcolor: '#0D47A1' }}>
                    <TableCell />
                    {visibleColumns.poNumber && <TableCell colSpan={visibleColumns.supplierName ? 3 : 2} sx={{ color: 'white', fontWeight: 'bold' }}>{t.grandTotal}</TableCell>}
                    {visibleColumns.warehouseName && <TableCell />}
                    {visibleColumns.branchName && <TableCell />}
                    {visibleColumns.expectedDate && <TableCell />}
                    {visibleColumns.subTotal && <TableCell />}
                    {visibleColumns.discount && <TableCell />}
                    {visibleColumns.tax && <TableCell />}
                    {visibleColumns.shipping && <TableCell />}
                    {visibleColumns.totalAmount && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>{formatNumber(stats.totalAmount)}</TableCell>}
                    {visibleColumns.status && <TableCell />}
                  </TableRow>
                )}

                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">{t.noData}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t.rowsPerPage}
            className="no-print"
          />
        </Paper>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 1cm; size: landscape; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
        .print-only { display: none; }
      `}</style>
    </Box>
  );
};

export default PurchaseOrdersReport;
